package fr.frux.feature.site.create

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowBack
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.LockOpen
import androidx.compose.material.icons.outlined.WavingHand
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxStepIndicator
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.components.InterceptNavigationBack
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapLocationPreview
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_create_access_ask
import fr.frux.feature.site.resources.site_create_access_ask_hint
import fr.frux.feature.site.resources.site_create_access_hint
import fr.frux.feature.site.resources.site_create_access_open
import fr.frux.feature.site.resources.site_create_access_open_hint
import fr.frux.feature.site.resources.site_create_access_title
import fr.frux.feature.site.resources.site_create_back
import fr.frux.feature.site.resources.site_create_exit_continue
import fr.frux.feature.site.resources.site_create_exit_quit
import fr.frux.feature.site.resources.site_create_exit_title
import fr.frux.feature.site.resources.site_create_header
import fr.frux.feature.site.resources.site_create_home
import fr.frux.feature.site.resources.site_create_home_hint
import fr.frux.feature.site.resources.site_create_manager_label
import fr.frux.feature.site.resources.site_create_name_hint
import fr.frux.feature.site.resources.site_create_name_label
import fr.frux.feature.site.resources.site_create_plantings_hint
import fr.frux.feature.site.resources.site_create_plantings_title
import fr.frux.feature.site.resources.site_create_retry
import fr.frux.feature.site.resources.site_create_step_place
import fr.frux.feature.site.resources.site_create_step_site
import fr.frux.feature.site.resources.site_create_step_state
import fr.frux.feature.site.resources.site_create_submit
import fr.frux.shared.domain.model.Site
import org.jetbrains.compose.resources.stringResource

private val PREVIEW_HEIGHT = 140.dp
private val CARD_SHAPE = RoundedCornerShape(16.dp)
private const val DISABLED_ALPHA = 0.38f

/**
 * Étape 2 de la création d'un lieu (maquette CreerLieu, ADR 0035), après l'épingle posée par
 * l'assistant. Le brouillon vit dans le [viewModel] : il survit à un remontage de l'écran.
 */
@Composable
fun CreateSiteScreen(
    args: CreateSiteArgs,
    onCreated: (Site) -> Unit,
    onExit: () -> Unit,
    viewModel: CreateSiteViewModel = rememberCreateSiteViewModel(args),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    CreateSiteEventsEffect(viewModel, onCreated = onCreated, onExit = onExit)

    if (state.exitPrompt) {
        InterceptNavigationBack(viewModel::dismissExit)
    } else if (state.hasInputs) {
        InterceptNavigationBack(viewModel::requestExit)
    }

    Column(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background).imePadding()) {
        CreateSiteHeader(onBack = viewModel::requestExit)
        Column(
            modifier =
                Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .navigationBarsPadding()
                    .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.lg),
        ) {
            MapLocationPreview(
                location = Location(args.latitude, args.longitude),
                pinIcon = rememberVectorPainter(FruxIcons.LocationOn),
                pinTint = MaterialTheme.fruxColors.accent,
                modifier = Modifier.fillMaxWidth().height(PREVIEW_HEIGHT).clip(CARD_SHAPE),
            )
            FruxTextField(
                value = state.name,
                onValueChange = viewModel::onNameChange,
                label = stringResource(Res.string.site_create_name_label),
                supportingText = stringResource(Res.string.site_create_name_hint),
            )
            PlantingsSection(state, viewModel)
            AccessSection(state, viewModel)
            SubmitSection(state, viewModel::submit)
        }
    }

    if (state.exitPrompt) {
        CreateSiteExitDialog(onQuit = viewModel::confirmExit, onContinue = viewModel::dismissExit)
    }
}

@Composable
private fun CreateSiteEventsEffect(
    viewModel: CreateSiteViewModel,
    onCreated: (Site) -> Unit,
    onExit: () -> Unit,
) {
    val currentOnCreated by rememberUpdatedState(onCreated)
    val currentOnExit by rememberUpdatedState(onExit)
    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                is CreateSiteEvent.Created -> currentOnCreated(event.site)
                CreateSiteEvent.Exited -> currentOnExit()
            }
        }
    }
}

@Composable
private fun CreateSiteHeader(onBack: () -> Unit) {
    val labels =
        listOf(
            stringResource(Res.string.site_create_step_place),
            stringResource(Res.string.site_create_step_site),
        )
    Column(modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.background)) {
        Column(
            modifier =
                Modifier
                    .windowInsetsPadding(WindowInsets.statusBars)
                    .padding(start = FruxSpacing.sm, top = FruxSpacing.sm, end = 12.dp, bottom = 12.dp),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                FruxIconButton(
                    icon = Icons.AutoMirrored.Outlined.ArrowBack,
                    contentDescription = stringResource(Res.string.site_create_back),
                    onClick = onBack,
                )
                Text(
                    text = stringResource(Res.string.site_create_header),
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.semantics { heading() },
                )
            }
            FruxStepIndicator(
                labels = labels,
                currentIndex = 1,
                stateDescription = stringResource(Res.string.site_create_step_state, 2, labels[1]),
                modifier = Modifier.padding(start = FruxSpacing.sm),
            )
        }
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
    }
}

@Composable
private fun SectionTitle(
    title: String,
    hint: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.semantics { heading() },
        )
        Text(
            text = hint,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun PlantingsSection(
    state: CreateSiteState,
    viewModel: CreateSiteViewModel,
) {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.md)) {
        SectionTitle(
            title = stringResource(Res.string.site_create_plantings_title),
            hint = stringResource(Res.string.site_create_plantings_hint),
        )
        state.plantings.forEach { line ->
            PlantingEditor(
                speciesName = line.species.commonNameFr,
                count = line.count,
                onCount = { viewModel.setCount(line.species.id, it) },
                onRemove = { viewModel.removeSpecies(line.species.id) },
            )
        }
        SpeciesPicker(
            query = state.speciesQuery,
            results = state.speciesResults,
            onQuery = viewModel::onSpeciesQuery,
            onPick = viewModel::addSpecies,
        )
    }
}

@Composable
private fun AccessSection(
    state: CreateSiteState,
    viewModel: CreateSiteViewModel,
) {
    // « Chez moi » rend le lieu privé : la règle d'accès déclarée ne s'applique plus.
    val choicesEnabled = !state.isHome
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        SectionTitle(
            title = stringResource(Res.string.site_create_access_title),
            hint = stringResource(Res.string.site_create_access_hint),
        )
        Column(modifier = Modifier.selectableGroup(), verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            AccessChoiceCard(
                icon = Icons.Outlined.LockOpen,
                title = stringResource(Res.string.site_create_access_open),
                hint = stringResource(Res.string.site_create_access_open_hint),
                selected = state.access == SiteAccessChoice.OPEN,
                enabled = choicesEnabled,
                onSelect = { viewModel.onAccess(SiteAccessChoice.OPEN) },
            )
            AccessChoiceCard(
                icon = Icons.Outlined.WavingHand,
                title = stringResource(Res.string.site_create_access_ask),
                hint = stringResource(Res.string.site_create_access_ask_hint),
                selected = state.access == SiteAccessChoice.ASK,
                enabled = choicesEnabled,
                onSelect = { viewModel.onAccess(SiteAccessChoice.ASK) },
            )
        }
        if (state.access == SiteAccessChoice.ASK && choicesEnabled) {
            FruxTextField(
                value = state.managerName,
                onValueChange = viewModel::onManagerName,
                label = stringResource(Res.string.site_create_manager_label),
            )
        }
        HomeSwitch(checked = state.isHome, onToggle = viewModel::onHomeToggled)
    }
}

@Composable
private fun AccessChoiceCard(
    icon: ImageVector,
    title: String,
    hint: String,
    selected: Boolean,
    enabled: Boolean,
    onSelect: () -> Unit,
) {
    val scheme = MaterialTheme.colorScheme
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = 64.dp)
                .alpha(if (enabled) 1f else DISABLED_ALPHA)
                .clip(CARD_SHAPE)
                .background(if (selected) scheme.primaryContainer else scheme.surface)
                .border(
                    width = if (selected) 2.dp else 1.dp,
                    color = if (selected) scheme.primary else scheme.outline,
                    shape = CARD_SHAPE,
                ).selectable(selected = selected, enabled = enabled, role = Role.RadioButton, onClick = onSelect)
                .padding(horizontal = FruxSpacing.md, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(imageVector = icon, contentDescription = null, tint = scheme.primary, modifier = Modifier.size(24.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(text = title, style = MaterialTheme.typography.titleMedium)
            Text(text = hint, style = MaterialTheme.typography.bodyMedium, color = scheme.onSurfaceVariant)
        }
        // La ligne porte la sémantique de sélection : le bouton radio n'est que visuel.
        RadioButton(selected = selected, onClick = null, enabled = enabled)
    }
}

@Composable
private fun HomeSwitch(
    checked: Boolean,
    onToggle: (Boolean) -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .clip(CARD_SHAPE)
                .toggleable(value = checked, role = Role.Switch, onValueChange = onToggle)
                .padding(vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(
            imageVector = Icons.Outlined.Home,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(24.dp),
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(text = stringResource(Res.string.site_create_home), style = MaterialTheme.typography.titleMedium)
            Text(
                text = stringResource(Res.string.site_create_home_hint),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Switch(checked = checked, onCheckedChange = null)
    }
}

@Composable
private fun SubmitSection(
    state: CreateSiteState,
    onSubmit: () -> Unit,
) {
    val sending = state.submission == CreateSiteSubmission.Sending
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        FruxButton(
            text = stringResource(Res.string.site_create_submit),
            onClick = onSubmit,
            enabled = state.canSubmit,
            loading = sending,
            modifier = Modifier.fillMaxWidth(),
        )
        val failure = state.submission as? CreateSiteSubmission.Failed
        if (failure != null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = failure.error.userMessage(),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.error,
                    modifier = Modifier.weight(1f),
                )
                FruxButton(
                    text = stringResource(Res.string.site_create_retry),
                    onClick = onSubmit,
                    style = FruxButtonStyle.Text,
                )
            }
        }
    }
}

@Composable
private fun CreateSiteExitDialog(
    onQuit: () -> Unit,
    onContinue: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onContinue,
        containerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
        shape = RoundedCornerShape(28.dp),
        title = {
            Text(stringResource(Res.string.site_create_exit_title), style = MaterialTheme.typography.headlineSmall)
        },
        dismissButton = {
            FruxButton(
                text = stringResource(Res.string.site_create_exit_quit),
                onClick = onQuit,
                style = FruxButtonStyle.DestructiveText,
            )
        },
        confirmButton = {
            FruxButton(text = stringResource(Res.string.site_create_exit_continue), onClick = onContinue)
        },
    )
}
